import React, {useEffect} from "react";
import {useConfigureNotebook} from "../../../../../../../hooks/use-notebook-config";
import Grid2 from "@mui/material/Unstable_Grid2";
import useBehavioralAnalyticsTool from "../../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";
import {DynamicPersonalConfigurationsSection} from "./dynamic-personal-configurations";
import {IndustrySettingsSection} from "./industry-settings";
import {Card, Divider, Tooltip, Typography} from "@mui/material";
import ConfigurableInfoBlock, {ConfigurableInfoBlockInputTypes} from "../../../../../../../components/app-specific/configurable-info-block";
import {InfoOutlined} from "@mui/icons-material";
import {usePersonalPanelConfigurations} from "../../../../../../../hooks/use-custom-saved-components-state/use-personal-panel-configurations";

const PersonalConfigurationsView = () => {
    const {configureNotebookTags} = useConfigureNotebook('personal-configurations-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.settingsPersonal);
        configureBehavioralTool((tags) => tags.settingsPersonal);
    }, [configureBehavioralTool, configureNotebookTags]);

    return (
        <>
            <Grid2 container spacing={4}>
                <DynamicPersonalConfigurationsSection/>
                <IndustrySettingsSection/>
                <PersonalPanelConfigurationsSection/>
            </Grid2>
        </>
    );
}


const PersonalPanelConfigurationsSection = () => {
    const operatorAccessLevels = useAccessLevels();
    const {
        loading,
        submitting,
        showWhatsNewDialog,
        showWelcomeToPanelTasksDialog,
        showHappyBirthdayDialog,
        setShowWhatsNewDialog,
        setShowWelcomeToPanelTasksDialog,
        setShowHappyBirthdayDialog,
    } = usePersonalPanelConfigurations();

    return (
        <>
            {
                loading &&
                Array.from({length: 1}).map((_, index) => (
                    <Grid2 xs={12} key={index}>
                        <div className={'loading access-level w-100'}>
                            <div>
                                <div/>
                            </div>
                        </div>
                    </Grid2>
                ))
            }
            {
                !loading &&
                <>
                    <Grid2 xs={12}>
                        <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12}>
                                    <Typography variant={'subtitle1'} fontWeight={600}>
                                        Personal Panel Configurations
                                    </Typography>
                                </Grid2>
                                <Grid2 xs={12}>
                                    <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                                </Grid2>
                                {
                                    operatorAccessLevels.packages.base.versionHistory &&
                                    <Grid2 xs={12} lg={6} paddingInline={3}>
                                        <ConfigurableInfoBlock
                                            title={"Show \"What's New\" Dialog after every release"}
                                            inputType={ConfigurableInfoBlockInputTypes.toggle}
                                            inputProps={{
                                                value: showWhatsNewDialog ? '1' : '0',
                                                exclusive: true,
                                                data: ['1', '0'].map(e => ({
                                                    value: e,
                                                    name: e === '1' ? "Yes" : "No",
                                                }))
                                            }}
                                            onValueChanged={(value) => setShowWhatsNewDialog(value === '1')}
                                            loading={submitting.includes('showWhatsNewDialog')}
                                            showCustomIcons
                                            customIcons={[
                                                <Tooltip key={1} title={'If enabled, a dialog showcasing the new features and improvements of the latest release will be shown after every update.'}>
                                                    <InfoOutlined color={'primary'}/>
                                                </Tooltip>
                                            ]}
                                        />
                                    </Grid2>
                                }
                                {
                                    operatorAccessLevels.packages.taskNote.welcomeToPanelTasksDialog &&
                                    <Grid2 xs={12} lg={6} paddingInline={3}>
                                        <ConfigurableInfoBlock
                                            title={"Show \"Welcome To Panel Tasks\" Dialog"}
                                            inputType={ConfigurableInfoBlockInputTypes.toggle}
                                            inputProps={{
                                                value: showWelcomeToPanelTasksDialog ? '1' : '0',
                                                exclusive: true,
                                                data: ['1', '0'].map(e => ({
                                                    value: e,
                                                    name: e === '1' ? "Yes" : "No",
                                                }))
                                            }}
                                            onValueChanged={(value) => setShowWelcomeToPanelTasksDialog(value === '1')}
                                            loading={submitting.includes('showWelcomeToPanelTasksDialog')}
                                            showCustomIcons
                                            customIcons={[
                                                <Tooltip key={1}
                                                         title={'If enabled, a dialog with useful tasks for getting started with the panel will be shown to users when they first access the panel.'}>
                                                    <InfoOutlined color={'primary'}/>
                                                </Tooltip>
                                            ]}
                                        />
                                    </Grid2>
                                }
                                {
                                    operatorAccessLevels.packages.base.birthdayDialog &&
                                    <Grid2 xs={12} lg={6} paddingInline={3}>
                                        <ConfigurableInfoBlock
                                            title={"Show \"Happy Birthday\" Dialog"}
                                            inputType={ConfigurableInfoBlockInputTypes.toggle}
                                            inputProps={{
                                                value: showHappyBirthdayDialog ? '1' : '0',
                                                exclusive: true,
                                                data: ['1', '0'].map(e => ({
                                                    value: e,
                                                    name: e === '1' ? "Yes" : "No",
                                                }))
                                            }}
                                            onValueChanged={(value) => setShowHappyBirthdayDialog(value === '1')}
                                            loading={submitting.includes('showHappyBirthdayDialog')}
                                            showCustomIcons
                                            customIcons={[
                                                <Tooltip key={1}
                                                         title={'If enabled, a dialog wishing users a happy birthday will be shown on their birthday.'}>
                                                    <InfoOutlined color={'primary'}/>
                                                </Tooltip>
                                            ]}
                                        />
                                    </Grid2>
                                }
                            </Grid2>
                        </Card>
                    </Grid2>
                </>
            }
        </>
    );
}

export default PersonalConfigurationsView;